import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { QueryProvider } from './app/providers/QueryProvider';
import { AuthProvider } from './app/providers/AuthProvider';
import { ProtectedRoute } from './app/routes/ProtectedRoute';
import { MainLayout } from './app/layouts/MainLayout';
import { HomePage } from './modules/home/HomePage';
import { MyContentPage } from './modules/home/MyContentPage';
import { CatalogPage } from './modules/catalog/CatalogPage';
import { TracksPage } from './modules/tracks/TracksPage';
import { TrackDetailPage } from './modules/tracks/TrackDetailPage';
import { CoursePage } from './modules/course/CoursePage';
import { LessonPage } from './modules/lesson/LessonPage';
import { LibraryPage } from './modules/library/LibraryPage';
import { CohortsPage } from './modules/cohorts/CohortsPage';
import { CertificatesPage } from './modules/certificates/CertificatesPage';
import { FavoritesPage } from './modules/favorites/FavoritesPage';
import { NotesPage } from './modules/notes/NotesPage';
import { LoginPage } from './modules/admin/LoginPage';
import { ForgotPasswordPage } from './modules/admin/ForgotPasswordPage';
import { ResetPasswordPage } from './modules/admin/ResetPasswordPage';
import { AdminDashboardPage } from './modules/admin/AdminDashboardPage';
import { AdminUsersPage } from './modules/admin/AdminUsersPage';
import { AdminUserFormPage } from './modules/admin/AdminUserFormPage';
import { AdminTrilhasPage } from './modules/admin/AdminTrilhasPage';
import { AdminTrilhaFormPage } from './modules/admin/AdminTrilhaFormPage';
import { AdminCursosPage } from './modules/admin/AdminCursosPage';
import { AdminCursoFormPage } from './modules/admin/AdminCursoFormPage';
import { AdminVideosPage } from './modules/admin/AdminVideosPage';
import { AdminVideoUploadPage } from './modules/admin/AdminVideoUploadPage';
import { AdminEmpresasPage } from './modules/admin/AdminEmpresasPage';
import { AdminEmpresaFormPage } from './modules/admin/AdminEmpresaFormPage';
import { AdminGruposPage } from './modules/admin/AdminGruposPage';
import { AdminGrupoFormPage } from './modules/admin/AdminGrupoFormPage';
import { AdminGrupoDetalhePage } from './modules/admin/AdminGrupoDetalhePage';
import { AdminBibliotecasPage } from './modules/admin/AdminBibliotecasPage';
import { AdminBibliotecaEmpresasPage } from './modules/admin/AdminBibliotecaEmpresasPage';
import { InstructorDashboardPage } from './modules/instructor/InstructorDashboardPage';
import { InstructorLessonsPage } from './modules/instructor/InstructorLessonsPage';
import { InstructorLessonFormPage } from './modules/instructor/InstructorLessonFormPage';
import { BibliotecaPage } from './modules/library/BibliotecaPage';
import { SentryTestButton } from './shared/components/SentryTestButton';

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Oops! Algo deu errado</h1>
            <p className="text-gray-600 mb-4">
              Desculpe pelo inconveniente. Nossa equipe foi notificada e estamos trabalhando para resolver o problema.
            </p>
            <details className="text-sm text-gray-500">
              <summary className="cursor-pointer font-medium mb-2">Detalhes técnicos</summary>
              <pre className="bg-gray-100 p-3 rounded overflow-auto">{error instanceof Error ? error.message : 'Erro desconhecido'}</pre>
            </details>
            <button
              onClick={() => window.location.href = '/'}
              className="mt-4 w-full btn-primary py-2"
            >
              Voltar para Home
            </button>
          </div>
        </div>
      )}
    >
      <QueryProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Rotas públicas */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
              <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
              <Route path="/certificados/validar/:codigo" element={<div>Validar Certificado (To be implemented)</div>} />

              {/* Rotas protegidas */}
              <Route
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<HomePage />} />
                <Route path="/meus-conteudos" element={<MyContentPage />} />
                <Route path="/catalogo" element={<CatalogPage />} />
              <Route path="/trilhas" element={<TracksPage />} />
              <Route path="/trilhas/:slug" element={<TrackDetailPage />} />
              <Route path="/cursos/:slug" element={<CoursePage />} />
              <Route path="/curso/:id" element={<CoursePage />} />
              <Route path="/aulas/:id" element={<LessonPage />} />
              <Route path="/biblioteca-antiga" element={<LibraryPage />} />
              <Route path="/biblioteca" element={<BibliotecaPage />} />
              <Route path="/turmas" element={<CohortsPage />} />
              <Route path="/turmas/:id" element={<div>Turma Detail (To be implemented)</div>} />
              <Route path="/favoritos" element={<FavoritesPage />} />
              <Route path="/anotacoes" element={<NotesPage />} />
              <Route path="/certificados" element={<CertificatesPage />} />
              <Route path="/perfil" element={<div>Perfil (To be implemented)</div>} />
              
              {/* Rotas Admin */}
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="/admin/usuarios" element={<AdminUsersPage />} />
              <Route path="/admin/usuarios/novo" element={<AdminUserFormPage />} />
              <Route path="/admin/usuarios/:id/editar" element={<AdminUserFormPage />} />
              <Route path="/admin/trilhas" element={<AdminTrilhasPage />} />
              <Route path="/admin/trilhas/nova" element={<AdminTrilhaFormPage />} />
              <Route path="/admin/trilhas/:id/editar" element={<AdminTrilhaFormPage />} />
              <Route path="/admin/cursos" element={<AdminCursosPage />} />
              <Route path="/admin/cursos/novo" element={<AdminCursoFormPage />} />
              <Route path="/admin/cursos/:id/editar" element={<AdminCursoFormPage />} />
              <Route path="/admin/videos" element={<AdminVideosPage />} />
              <Route path="/admin/videos/upload" element={<AdminVideoUploadPage />} />
              <Route path="/admin/empresas" element={<AdminEmpresasPage />} />
              <Route path="/admin/empresas/nova" element={<AdminEmpresaFormPage />} />
              <Route path="/admin/empresas/:id/editar" element={<AdminEmpresaFormPage />} />
              <Route path="/admin/empresas/:empresaId/grupos" element={<AdminGruposPage />} />
              <Route path="/admin/empresas/:empresaId/grupos/novo" element={<AdminGrupoFormPage />} />
              <Route path="/admin/empresas/:empresaId/grupos/:grupoId" element={<AdminGrupoDetalhePage />} />
              <Route path="/admin/empresas/:empresaId/grupos/:grupoId/editar" element={<AdminGrupoFormPage />} />
              
              {/* Rotas Biblioteca de Documentos */}
              <Route path="/admin/bibliotecas" element={<AdminBibliotecasPage />} />
              <Route path="/admin/bibliotecas/:grupoId/empresas" element={<AdminBibliotecaEmpresasPage />} />
              
              {/* Rotas Instrutor */}
              <Route path="/instructor" element={<InstructorDashboardPage />} />
              <Route path="/instructor/lessons" element={<InstructorLessonsPage />} />
              <Route path="/instructor/lessons/new" element={<InstructorLessonFormPage />} />
              <Route path="/instructor/lessons/:id/edit" element={<InstructorLessonFormPage />} />
            </Route>

            {/* Redirect para home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          {/* Botão de teste do Sentry (apenas em desenvolvimento) */}
          <SentryTestButton />
        </BrowserRouter>
      </AuthProvider>
    </QueryProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
